import { Outlet, Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Calendar,
  CreditCard,
  Settings,
  LogOut,
  Menu,
  X,
  Heart,
  Plus,
  Gift
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/use-auth';
import { cn } from '@/lib/utils';
import { NotificationsBell } from '@/components/notifications/NotificationsBell';
import { SEOHead } from '@/components/seo/seo-head';

const navItems = [
  { href: '/app/dashboard', label: 'Dashboard', icon: LayoutDashboard, color: 'lavender' },
  { href: '/app/events', label: 'Evenimente', icon: Calendar, color: 'terracotta' },
  { href: '/app/subscriptions', label: 'Abonamente', icon: CreditCard, color: 'sage' },
  { href: '/app/settings', label: 'Setări', icon: Settings, color: 'dusty-blue' },
  { href: '/app/gifts', label: 'Cadouri', icon: Gift, color: 'terracotta' },
];

export function DashboardLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const currentNavItem = navItems.find((item) => 
    location.pathname === item.href || location.pathname.startsWith(item.href + '/')
  );

  return (
    <div className="min-h-screen flex bg-secondary/20">
      <SEOHead title="Dashboard" noIndex />
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden animate-fade-in"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-72 bg-white shadow-premium transform transition-transform duration-500 ease-in-out lg:translate-x-0 lg:static border-r border-border/40',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="h-20 flex items-center justify-between px-8 border-b border-border/40">
            <Link to="/app/dashboard" className="flex items-center gap-2.5 text-xl font-serif font-bold tracking-tight">
              <div className="w-8 h-8 rounded-lg bg-terracotta flex items-center justify-center shadow-lg shadow-terracotta/20">
                <Heart className="w-4 h-4 text-white fill-white/20" />
              </div>
              <span className="bg-clip-text text-transparent bg-gradient-to-br from-foreground to-foreground/70">Invitații Online</span>
            </Link>
            <button 
              className="lg:hidden p-2 rounded-xl hover:bg-secondary transition-colors" 
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Quick action - Redesigned for more professional look */}
          <div className="px-6 py-8">
            <Button 
              variant="foreground" 
              className="w-full justify-center gap-2.5 h-12 rounded-2xl shadow-premium hover:shadow-premium-lg transition-all duration-300 group" 
              asChild
            >
              <Link to="/app/events/new">
                <Plus className="w-5 h-5 transition-transform group-hover:rotate-90" />
                <span className="font-medium">Creează Eveniment</span>
              </Link>
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 space-y-1.5 overflow-y-auto scrollbar-thin">
            <p className="px-4 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/60 mb-3 mt-2">Meniu Principal</p>
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href || 
                              (item.href !== '/app/dashboard' && location.pathname.startsWith(item.href));
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    'flex items-center gap-3.5 px-4 py-3.5 rounded-2xl transition-all duration-300 group relative overflow-hidden',
                    isActive
                      ? 'bg-foreground text-background shadow-premium'
                      : 'hover:bg-secondary/80 text-muted-foreground hover:text-foreground',
                  )}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className={cn('h-5 w-5 transition-colors duration-300', 
                    isActive ? 'text-background' : 'group-hover:text-foreground'
                  )} />
                  <span className="font-medium text-sm tracking-tight">{item.label}</span>
                  {isActive && (
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-5 bg-primary rounded-l-full" />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User section - Redesigned */}
          <div className="p-6 mt-auto">
            <div className="p-4 rounded-3xl bg-secondary/50 border border-border/40 mb-4 group transition-all duration-300 hover:bg-white hover:shadow-premium hover:border-transparent">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-primary/10 flex items-center justify-center border border-primary/20 shadow-inner group-hover:scale-105 transition-transform duration-500">
                  <span className="text-sm font-bold text-primary tracking-tighter">
                    {user?.firstName?.[0]}
                    {user?.lastName?.[0]}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold truncate tracking-tight">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-[10px] text-muted-foreground truncate font-medium uppercase tracking-wider">{user?.role || 'Utilizator'}</p>
                </div>
              </div>
            </div>
            
            <Button 
              variant="ghost" 
              className="w-full justify-start gap-3 h-12 rounded-2xl text-muted-foreground hover:text-destructive hover:bg-destructive/5 transition-colors" 
              onClick={logout}
            >
              <LogOut className="h-4 w-4" />
              <span className="font-medium text-sm tracking-tight">Deconectare</span>
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="h-20 bg-white/70 backdrop-blur-xl border-b border-border/40 flex items-center justify-between px-6 lg:px-12 sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <button 
              className="lg:hidden p-2.5 -ml-2 rounded-2xl hover:bg-secondary transition-colors" 
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-6 w-6" />
            </button>
            <div className="flex flex-col">
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/60 leading-none mb-1">Pagina</span>
              <h2 className="text-lg font-serif font-bold tracking-tight text-foreground leading-none">
                {currentNavItem?.label || 'Dashboard'}
              </h2>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-2xl bg-secondary/50 border border-border/40">
               <div className="w-2 h-2 rounded-full bg-sage animate-pulse" />
               <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Status: Online</span>
            </div>
            <NotificationsBell />
          </div>
        </header>

        <main className="flex-1 p-6 lg:p-12 overflow-y-auto scrollbar-hide">
          <div className="max-w-6xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
